import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ListsViewModel } from '@app/lists/model/lists-view.model';
import { selectMasterListInventoryScaffold } from '@app/lists/pages/master-list/selectors/master-list.selectors';
import { ListsService } from '@app/lists/services/lists.service';
import { selectListInventoryScaffold } from '@app/lists/shared/list-detail-management/selectors/list-detail-management-view-model.selector';
import { CreateInventoryModal } from '@inventory/constants/inventory-constants';
import {
  InventoryViewModel,
  inventoryGroupScaffold,
  inventoryScaffold,
} from '@inventory/models/inventory-view.model';
import { CreateInventoryValidationErrors } from '@inventory/models/validation-errors-model';
import { NonUsfProductViewModelSelectors } from '@inventory/pages/manage-non-usf-products/selectors/non-usf-selectors';
import {
  createInventoryItemWithProductInformation,
  enrichProductGroupItemWithPricing,
} from '@inventory/selectors/helpers/inventory-create-update-helper';
import { InventoryCreateSelectors } from '@inventory/selectors/inventory-create.selector';
import { Store } from '@ngrx/store';
import { ListTypes } from '@shared/constants/lists-constants';
import { Product } from '@shared/models/product.model';
import { productsAreInStateWithPricing } from '@shared/selectors/helpers/product-info.selectors.helper';
import {
  Inventory,
  InventoryGroup,
  InventoryGroupItem,
  SubCategory,
} from '@usf/inventory-types';
import {
  ANALYTICS_ACTIONS,
  INVENTORY_ACTIONS,
  InventoryAnalyticsConstants,
  InventoryTracingConstants,
  InventoryUIConstantsFacadeService,
  copyInventoryTransform,
  createInventoryTransformer,
  pageLoadedTransformer,
  selectInventoryById,
} from '@usf/ngrx-inventory';
import {
  ListConstants,
  RECENT_PURCHASE_ACTIONS,
  RecentPurchaseState,
  recentPurchaseAdapter,
  selectRecentPurchaseState,
} from '@usf/ngrx-list';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, finalize, map, take, tap } from 'rxjs/operators';
import { State } from 'src/app/store';
import { comparePurchaseDates } from '../../../../lists/pages/recently-purchased/selectors/helpers/recent-purchase.selectors.helper';
import { RecentlyPurchasedService } from '../../../../lists/pages/recently-purchased/services/recently-purchased.service';
import { PlatformEnum } from '../../../../shared/constants/platform-type.enum';
import { ProductService } from '../../../../shared/services/product/product.service';
import { AddToInventoryViewModel } from '../../../models/add-to-inventory-view-model.model';
import {
  getAllNonUSFProductsAndConvertToFakeProducts,
  getAllNonUSFProductsAsUSFProductsMap,
  selectInventoryNameDateValidator,
  selectPastInventoriesViewModels,
} from '../../../selectors/inventory-view-model.selector';
import {
  selectAddToInventoryViewModel,
  selectRecentPurchases,
  selectSelectedRecentPurchaseIds,
} from '../selectors/add-to-inventory-view-model.selector';
import { selectSelectedInventory } from '../selectors/past-inventory.selector';
import {
  ProductPricingService,
  isProductPricingLoaded,
  selectProductPricingEntities,
} from '@usf/ngrx-pricing';
import {
  selectNonUSFProductsState,
  NonUSFProductState,
} from '@usf/ngrx-product';
import { productWithAlternativesSelector } from '@shared/selectors/product.selectors';

@Injectable({
  providedIn: 'root',
})
export class CreateInventoryService {
  public inventoryMinDate: String;
  public inventoryMaxDate: String;
  // Values designate how many days we should adjust our current date by.
  private inventoryMinValueAdjust = 730;
  private inventoryMaxValueAdjust = 730;
  private inventoryDate: Date;
  createInventoryForm: FormGroup;
  resetCounts = true;

  // modal values:
  public createInventoryModal = CreateInventoryModal;

  constructor(
    private store: Store<State>,
    private router: Router,
    private route: ActivatedRoute,
    public datePipe: DatePipe,
    private inventoryConstantsFacadeService: InventoryUIConstantsFacadeService,
    private listsService: ListsService,
    private recentlyPurchasedService: RecentlyPurchasedService,
    private productService: ProductService,
    private inventoryCreateSelectors: InventoryCreateSelectors,
    private nonUSFSelectors: NonUsfProductViewModelSelectors,
    private productPricingService: ProductPricingService,
  ) {
    this.setupValues();
  }

  public setupValues() {
    this.resetCounts = true;
    this.inventoryDate = new Date();
    const tempMinDate = new Date(this.inventoryDate);
    tempMinDate.setDate(tempMinDate.getDate() - this.inventoryMinValueAdjust);
    const tempMaxDate = new Date(this.inventoryDate);
    tempMaxDate.setDate(tempMaxDate.getDate() + this.inventoryMaxValueAdjust);
    this.inventoryMinDate = tempMinDate.toISOString();
    this.inventoryMaxDate = tempMaxDate.toISOString();
    this.createInventoryForm = new FormGroup(
      {
        name: new FormControl(
          '',
          Validators.compose([Validators.required, Validators.maxLength(30)]),
        ),
        date: new FormControl(
          this.inventoryDate.toISOString(),
          Validators.compose([Validators.required]),
        ),
        creationType: new FormControl(
          undefined,
          Validators.compose([Validators.nullValidator]),
          (control: AbstractControl) => {
            return of(
              control.value === undefined ||
                control.value === null ||
                Object.keys(control.value).length === 0
                ? { invalid: true }
                : null,
            );
          },
        ),
        selectedInventory: new FormControl(null),
        selectedShoppingList: new FormControl(null),
        pastPurchaseDays: new FormControl(null),
        numberOfSelectedProducts: new FormControl(null),
      },
      null,
      this.nameDateAsyncValidator.bind(this),
    );
  }

  public clearValues() {
    this.createInventoryForm.patchValue({
      creationType: {},
      selectedInventory: null,
      selectedShoppingList: null,
      pastPurchaseDays: null,
      numberOfSelectedProducts: null,
    });
    this.createInventoryForm.controls.numberOfSelectedProducts.setValidators(
      null,
    );
    this.createInventoryForm.controls.numberOfSelectedProducts.updateValueAndValidity();
  }

  public clearValidation() {
    this.store.dispatch(
      INVENTORY_ACTIONS.setNameValidator({ newInventoryName: undefined }),
    );
    this.store.dispatch(
      INVENTORY_ACTIONS.setDateValidator({ newInventoryDate: undefined }),
    );
  }

  private nameDateAsyncValidator = (control: AbstractControl) => {
    this.store.dispatch(
      INVENTORY_ACTIONS.setDateValidator({
        newInventoryDate: control.get('date').value,
      }),
    );
    this.store.dispatch(
      INVENTORY_ACTIONS.setNameValidator({
        newInventoryName: control.get('name').value.trim(),
      }),
    );
    return this.store.select(selectInventoryNameDateValidator).pipe(
      take(1),
      map(value => {
        return value ? { invalid: true } : null;
      }),
    );
  };

  public nameDateIssue$ = (): Observable<CreateInventoryValidationErrors> => {
    return this.store.select(selectInventoryNameDateValidator).pipe(
      map(nameDateInvalid => {
        return { nameDateInvalid };
      }),
    );
  };

  public selectSelectedInventory$ = () =>
    this.store.select(selectSelectedInventory());

  public setSelectedDate(date: Date) {
    if (this.createInventoryForm.get('creationType').invalid)
      this.store.dispatch(
        INVENTORY_ACTIONS.setDateValidator({
          newInventoryDate: date,
        }),
      );
    this.createInventoryForm.patchValue({ date });
  }

  public updateNameValidator = (name: string) => {
    if (this.createInventoryForm.get('creationType').invalid)
      this.store.dispatch(
        INVENTORY_ACTIONS.setNameValidator({
          newInventoryName: name.trim(),
        }),
      );
  };

  public setSelectedCreateInventoryOption(
    selectedCreateInventoryOption: SubCategory,
  ) {
    this.createInventoryForm.patchValue({
      creationType: selectedCreateInventoryOption,
    });
  }

  public getInventories$(keyword?: string) {
    return this.store.select(selectPastInventoriesViewModels(keyword));
  }

  public updateSelectedInventory(inventoryId: string, isSelected: boolean) {
    this.store.dispatch(
      INVENTORY_ACTIONS.updateIsSelected({ inventoryId, isSelected }),
    );
  }

  public setSelectedInventory(selectedInventory: InventoryViewModel) {
    this.createInventoryForm.patchValue({ selectedInventory });
  }

  public getShoppingLists$(keyword?: string) {
    return this.listsService.listsForInventory$(keyword);
  }

  public onSelectMasterList(id: number, value: boolean) {
    this.listsService.selectAMasterList(id, value);
    this.listsService.selectAList('no-selection', false);
    this.listsService.selectAnOrderGuide('no-selection', false);
    this.listsService.selectRecentPurchase(false);
  }

  public onSelectShoppingList(key: string, value: boolean) {
    this.listsService.selectAList(key, value);
    this.listsService.selectAMasterList(9999999999, false);
    this.listsService.selectAnOrderGuide('no-selection', false);
    this.listsService.selectRecentPurchase(false);
  }

  public onSelectOrderGuide(key: string, value: boolean) {
    this.listsService.selectAnOrderGuide(key, value);
    this.listsService.selectAMasterList(9999999999, false);
    this.listsService.selectAList('no-selection', false);
    this.listsService.selectRecentPurchase(false);
  }

  public onSelectRecentPurchase(value: boolean) {
    this.listsService.selectRecentPurchase(value);
    this.listsService.selectAnOrderGuide('no-selection', false);
    this.listsService.selectAMasterList(9999999999, false);
    this.listsService.selectAList('no-selection', false);
  }

  public setSelectedShoppingList(selectedShoppingList: ListsViewModel) {
    this.createInventoryForm.patchValue({ selectedShoppingList });
  }

  public setPastPurchaseDays(pastPurchaseDays: string) {
    this.createInventoryForm.patchValue({ pastPurchaseDays });
    this.createInventoryForm.controls.numberOfSelectedProducts.setValidators([
      Validators.required,
    ]);
    this.createInventoryForm.controls.numberOfSelectedProducts.updateValueAndValidity();
  }

  public setNumberOfSelectedProducts(numberOfSelectedProducts: number) {
    this.createInventoryForm.patchValue({ numberOfSelectedProducts });
  }

  public getInventoryConstants$() {
    return this.inventoryConstantsFacadeService.createInventoryInfo$();
  }

  public getValidRecentPurchaseDates$() {
    return this.recentlyPurchasedService.daysThatAreWithinRecentPuchaseRange$();
  }

  public searchRecentPurchase(keyword: string) {
    this.recentlyPurchasedService.searchInRecentPurchase(keyword, true, false);
  }

  public selectAddToInventoryViewModel$(
    platformType: PlatformEnum,
  ): Observable<AddToInventoryViewModel> {
    return this.store.select(
      selectAddToInventoryViewModel(
        this.createInventoryForm?.controls?.pastPurchaseDays?.value,
        platformType,
      ),
    );
  }

  public selectRecentPurchases$(): Observable<RecentPurchaseState[]> {
    return this.store.select(selectRecentPurchases());
  }

  getRecentlyPurchasedProductData() {
    this.recentlyPurchasedService
      .recentlyPurchasedProductNumbers$()
      .pipe(take(1))
      .subscribe(productNumbers => {
        this.productService.loadProducts(productNumbers);
      });
  }

  selectPastPurchaseProduct(productNumber: number, value: boolean) {
    this.store.dispatch(
      RECENT_PURCHASE_ACTIONS.setIsSelectedForARecentPurchase({
        key: productNumber,
        value,
      }),
    );
  }

  selectMultiplePastPurchaseProducts(productNumbers: number[], value: boolean) {
    productNumbers.forEach(prodNumber =>
      this.store.dispatch(
        RECENT_PURCHASE_ACTIONS.setIsSelectedForARecentPurchase({
          key: prodNumber,
          value,
        }),
      ),
    );
  }

  selectAllPastPurchaseProducts(value: boolean) {
    this.store.dispatch(
      RECENT_PURCHASE_ACTIONS.setIsSelectedForAllRecentPurchases({ value }),
    );
  }

  public goToInventoryWorksheet(inventoryWorksheetPath: string[]) {
    this.router.navigate(inventoryWorksheetPath, {
      relativeTo: this.route,
    });
    // Value reset on navigation to inventory worksheet
    this.setupValues();
  }

  public onSubmit() {
    const creationType =
      this.createInventoryForm.get('creationType').value?.code;
    if (creationType === CreateInventoryModal.pastInventory.valueOf()) {
      this.createNewInventoryFromPastInventory();
    } else if (creationType === CreateInventoryModal.shoppingList.valueOf()) {
      this.createNewInventoryFromShoppingList();
    } else if (creationType === CreateInventoryModal.pastPurchase.valueOf()) {
      const recentPurchaseIds = this.store.select(
        selectSelectedRecentPurchaseIds(),
      );
      this.createNewInventoryFromRecentPurchaseIds(recentPurchaseIds);
    } else if (creationType === CreateInventoryModal.manual.valueOf()) {
      this.createNewInventoryManualAdd();
    }
  }

  createNewInventoryFromPastInventory() {
    const selectedInventory =
      this.createInventoryForm.get('selectedInventory').value;
    this.store
      .select(selectInventoryById(selectedInventory.inventoryId))
      .pipe(take(1))
      .subscribe(inventory => {
        // Extract all product numbers from inventory items
        const productNumbers = new Set<number>();
        inventory.groups?.forEach(group => {
          group.groupItems?.forEach(item => {
            if (!item.nonUsfFlag) {
              productNumbers.add(item.itemNumber);
            }
          });
        });

        const productNumbersArray = Array.from(productNumbers);
        // Load latest prices for all products
        this.productPricingService.loadPrices(productNumbersArray);

        // Wait for prices to be loaded and then create inventory
        combineLatest([
          this.store.select(isProductPricingLoaded(productNumbersArray)),
          this.store.select(productWithAlternativesSelector),
          this.store.select(getAllNonUSFProductsAsUSFProductsMap),
        ])
          .pipe(
            filter(([isLoaded, _, nonUsfProductMap]) => isLoaded),
            map(([_, products, nonUsfProductMap]) => [
              products,
              nonUsfProductMap,
            ]),
            take(1),
          )
          .subscribe(
            ([products, nonUsfProductMap]: [
              Map<number, Product>,
              Map<number, Product>,
            ]) => {
              const copiedInventory = {
                inventoryName: this.createInventoryForm.get('name').value,
                inventoryDate: this.createInventoryForm.get('date').value,
                groups: [] as InventoryGroup[],
              } as Inventory;

              inventory.groups?.forEach(group => {
                const groupToAdd = {
                  ...group,
                  groupItems: [] as InventoryGroupItem[],
                };
                group.groupItems?.forEach(item => {
                  let itemToAdd = { ...item };
                  if (this.resetCounts) {
                    itemToAdd.caseOnHand = 0;
                    itemToAdd.eachOnHand = 0;
                  }

                  const product = item.nonUsfFlag
                    ? nonUsfProductMap.get(item.itemNumber)
                    : products.get(item.itemNumber);

                  if (product) {
                    itemToAdd = enrichProductGroupItemWithPricing(
                      itemToAdd,
                      product,
                      'LP',
                    );
                  }
                  itemToAdd.priceOverrideInd = 'N';
                  itemToAdd.overrideCasePrice = 0;

                  groupToAdd.groupItems.push(itemToAdd);
                });
                copiedInventory.groups.push(groupToAdd);
              });

              const pastInventoryDate = new Date(
                selectedInventory.inventoryDate,
              ).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
              });
              this.store.dispatch(
                INVENTORY_ACTIONS.create({
                  inventory: copiedInventory,
                  copiedInvName:
                    selectedInventory.inventoryName + ' - ' + pastInventoryDate,
                  createSource: this.getCreateSourceForAnalytics(),
                  tracking: {
                    tracing: {
                      data: {
                        traceContext: InventoryTracingConstants.inventory,
                        isEndOfTrace: false,
                        isStartOfTrace: true,
                        attributes: {
                          newInventoryName: `${copiedInventory.inventoryName} - ${copiedInventory.inventoryDate}`,
                          pastInventoryId: selectedInventory.inventoryId,
                          inventoryId: '',
                          event: InventoryTracingConstants.copyInventoryEvent,
                          spanName: InventoryTracingConstants.copyInventorySpan,
                        },
                      },
                      transformFunc: copyInventoryTransform,
                    },
                  },
                }),
              );
            },
          );
      });
  }

  createNewInventoryFromMasterList() {
    const masterList = this.createInventoryForm.get(
      'selectedShoppingList',
    ).value;
    this.store
      .select(selectMasterListInventoryScaffold(masterList.listKey.listId))
      .pipe(take(1))
      .subscribe((inventoryScaffold: inventoryScaffold) => {
        this.generateInventoryBasedOnProductNumbersAndInventoryScaffold(
          [],
          inventoryScaffold,
        );
      });
  }

  createNewInventoryFromShoppingList() {
    const selectedShoppingList = this.createInventoryForm.get(
      'selectedShoppingList',
    ).value;
    if (selectedShoppingList.listName === ListTypes.recentlyPurchased) {
      const recentPurchaseIds = this.store.select(
        recentPurchaseAdapter.getSelectors(selectRecentPurchaseState).selectIds,
      );
      this.createNewInventoryFromRecentPurchaseIds(recentPurchaseIds);
    } else if (
      selectedShoppingList.listKey.listTypeId === 'ML' ||
      selectedShoppingList.listKey.listTypeId === 'R-ML'
    ) {
      this.createNewInventoryFromMasterList();
    } else {
      this.createNewInventoryFromList();
    }
  }

  createNewInventoryFromList() {
    const selectedShoppingList = this.createInventoryForm.get(
      'selectedShoppingList',
    ).value;
    this.store
      .select(
        selectListInventoryScaffold(
          selectedShoppingList.listKey.listTypeId,
          selectedShoppingList.listKey.listId,
        ),
      )
      .pipe(take(1))
      .subscribe(inventoryScaffold => {
        this.generateInventoryBasedOnProductNumbersAndInventoryScaffold(
          [],
          inventoryScaffold,
        );
      });
  }

  createNewInventoryManualAdd() {
    let inventoryFromManual: Inventory;
    const inventoryGroup: InventoryGroup = {
      groupName: ListConstants.unassignedGroup,
      groupUpdateDtm: new Date(),
    };
    inventoryFromManual = {
      inventoryName: this.createInventoryForm.get('name').value,
      inventoryDate: this.createInventoryForm.get('date').value,
      groups: [inventoryGroup],
    } as Inventory;

    this.store.dispatch(
      INVENTORY_ACTIONS.create({
        inventory: inventoryFromManual,
        createSource: this.getCreateSourceForAnalytics(),
        tracking: this.getCreateInventoryTrackingObject(),
      }),
    );
  }

  createNewInventoryFromRecentPurchaseIds(
    recentPurchaseIds: Observable<number[] | string[]>,
  ) {
    recentPurchaseIds.pipe(take(1)).subscribe((recentPurchaseIds: number[]) => {
      this.productService.loadProducts(recentPurchaseIds);
      this.store
        .select(
          this.inventoryCreateSelectors.productsForInventoryById(
            recentPurchaseIds,
          ),
        )
        .pipe(
          filter((productMap: Map<number, Product>) =>
            productsAreInStateWithPricing(productMap),
          ),
          take(1),
        )
        .subscribe((productMap: Map<number, Product>) => {
          const itemsOnInventory: Product[] = [];
          let productsNotLoaded = 0;
          for (const id of recentPurchaseIds) {
            const product = productMap.get(id);
            if (product?.notFound) {
              productsNotLoaded++;
            }
            itemsOnInventory.push(product);
          }
          itemsOnInventory.sort(comparePurchaseDates);

          const inventoryToCreate: Inventory =
            this.generateInventoryFromProducts(itemsOnInventory);

          this.store.dispatch(
            INVENTORY_ACTIONS.create({
              inventory: inventoryToCreate,
              productsNotFound: productsNotLoaded,
              createSource: this.getCreateSourceForAnalytics(),
              tracking: this.getCreateInventoryTrackingObject(),
            }),
          );
        });
    });
  }

  generateInventoryFromProducts(productsOnInventory: Product[]): Inventory {
    const groupItems: InventoryGroupItem[] = productsOnInventory.map(
      (product: Product) =>
        createInventoryItemWithProductInformation(product, 'LP', false),
    );
    return {
      inventoryName: this.createInventoryForm.get('name').value,
      inventoryDate: this.createInventoryForm.get('date').value,
      groups: [
        {
          groupName: ListConstants.unassignedGroup,
          groupUpdateDtm: new Date(),
          groupItems,
        } as InventoryGroup,
      ],
    } as Inventory;
  }

  getCreateInventoryTrackingObject() {
    return {
      tracing: {
        data: {
          traceContext: InventoryTracingConstants.inventory,
          isEndOfTrace: false,
          isStartOfTrace: true,
          attributes: {
            inventoryName:
              this.createInventoryForm.get('name').value +
              ' ' +
              this.createInventoryForm.get('date').value,
            inventoryId: '',
            createFrom: this.getCreateFromValue(),
            event: InventoryTracingConstants.createInventoryEvent,
            spanName: InventoryTracingConstants.createInventorySpan,
          },
        },
        transformFunc: createInventoryTransformer,
      },
    };
  }

  createInventoryScaffoldFromPastPurchase = (
    pastPurchases: RecentPurchaseState[],
  ): inventoryScaffold => {
    const productsToLoad: Set<number> = new Set();
    const inventoryGroup: inventoryGroupScaffold = {
      groupName: ListConstants.unassignedGroup,
      items: pastPurchases.map(value => {
        productsToLoad.add(value.productNumber);
        return { productNumber: value.productNumber, nonUSF: false };
      }),
    };
    return {
      inventoryGroups: [inventoryGroup],
      productsToLoad: Array.from(productsToLoad),
    };
  };

  generateInventoryBasedOnProductNumbersAndInventoryScaffold = (
    nonUSFProducts: number[],
    inventoryScaffold: inventoryScaffold,
  ) => {
    this.productService
      .loadProducts$(inventoryScaffold.productsToLoad)
      .pipe(take(1))
      .subscribe(() => {
        combineLatest([
          this.store.select(
            this.nonUSFSelectors.getFakeProductsFromNonUSfProductsBasedOnId(
              nonUSFProducts,
            ),
          ),
          this.store.select(
            this.inventoryCreateSelectors.productsForInventoryById(
              inventoryScaffold.productsToLoad,
            ),
          ),
        ])
          .pipe(
            filter(([fakeProducts, productsToFilter]) =>
              productsAreInStateWithPricing(productsToFilter),
            ),
            tap(([fakeProducts, productsLoaded]) => {
              let productNumbersNotLoaded = [];
              productsLoaded.forEach((value, key) => {
                if (value.notFound) {
                  if (!productNumbersNotLoaded.includes(key)) {
                    productNumbersNotLoaded.push(key);
                  }
                }
              });

              fakeProducts.forEach(product => {
                productsLoaded.set(product.productNumber, product);
              });

              this.generateInventoryBasedOnProductDataAndScaffold(
                productsLoaded,
                inventoryScaffold,
                productNumbersNotLoaded.length,
              );
            }),
            take(1),
            finalize(() => {}),
          )
          .subscribe(() => {});
      });
  };

  generateInventoryBasedOnProductDataAndScaffold = (
    products: Map<number, Product>,
    inventoryScaffold: inventoryScaffold,
    productsNotFound: number,
  ) => {
    let inventoryToCreate: Inventory = {
      inventoryName: this.createInventoryForm.get('name').value,
      inventoryDate: this.createInventoryForm.get('date').value,
      groups: inventoryScaffold.inventoryGroups.map(value => {
        const items = [];
        value.items.forEach(item => {
          if (products.has(item.productNumber)) {
            items.push(
              createInventoryItemWithProductInformation(
                products.get(item.productNumber),
                'LP',
                item.nonUSF,
              ),
            );
          }
        });

        return {
          groupName: value.groupName,
          groupUpdateDtm: new Date(),
          groupItems: items,
        };
      }),
    } as Inventory;
    this.store.dispatch(
      INVENTORY_ACTIONS.create({
        inventory: inventoryToCreate,
        productsNotFound: productsNotFound,
        createSource: this.getCreateSourceForAnalytics(),
        tracking: this.getCreateInventoryTrackingObject(),
      }),
    );
  };

  getCreateFromValue = () => {
    switch (this.createInventoryForm.get('creationType').value?.code) {
      case CreateInventoryModal.pastInventory.valueOf():
        return InventoryTracingConstants.pastInventory;
      case CreateInventoryModal.pastPurchase.valueOf():
        return InventoryTracingConstants.pastPurchhase;
      case CreateInventoryModal.manual.valueOf():
        return InventoryTracingConstants.addItems;
      case CreateInventoryModal.shoppingList.valueOf():
        return InventoryTracingConstants.shoppingList;
      default:
        return '';
    }
  };

  getCreateSourceForAnalytics = () => {
    switch (this.createInventoryForm.get('creationType').value?.code) {
      case CreateInventoryModal.pastInventory.valueOf():
        return InventoryAnalyticsConstants.pastInventory;
      case CreateInventoryModal.pastPurchase.valueOf():
        return InventoryAnalyticsConstants.pastPurchase;
      case CreateInventoryModal.manual.valueOf():
        return InventoryAnalyticsConstants.manual;
      case CreateInventoryModal.shoppingList.valueOf():
        return InventoryAnalyticsConstants.shoppingList;
      default:
        return '';
    }
  };

  clearRecentPurchase = () => {
    this.store.dispatch(
      RECENT_PURCHASE_ACTIONS.setIsSelectedForAllRecentPurchases({
        value: false,
      }),
    );
  };

  trackCreateInventoryPageLoaded = () => {
    this.store.dispatch(
      ANALYTICS_ACTIONS.trackCreateInventoryPageLoaded({
        tracking: {
          analytics: {
            data: {
              capability: InventoryAnalyticsConstants.capability,
              event: InventoryAnalyticsConstants.pageLoaded,
              pageName: InventoryAnalyticsConstants.createInventoryPage,
            },
            transformFunc: pageLoadedTransformer,
          },
        },
      }),
    );
  };
}
